import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  useTheme,
  alpha,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import Grid from '@mui/material/Grid';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GroupIcon from '@mui/icons-material/Group';
import BarChartIcon from '@mui/icons-material/BarChart';
import TranslateIcon from '@mui/icons-material/Translate';
import LockIcon from '@mui/icons-material/Lock';
import DevicesIcon from '@mui/icons-material/Devices';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { GOOGLE_CLIENT_ID } from '../../utils';
import { useAuth } from '../../hooks/useAuth';

const featureList = {
  en: [
    { icon: <FitnessCenterIcon color="primary" fontSize="large" />, title: 'Workout Tracking', desc: 'Record exercises with sets, reps, and weights.' },
    { icon: <CalendarMonthIcon color="primary" fontSize="large" />, title: 'Calendar View', desc: 'Visualize your workout history on a monthly calendar.' },
    { icon: <BarChartIcon color="primary" fontSize="large" />, title: 'AI Workout Analysis', desc: 'Get AI-powered insights into your workout patterns and progress.' },
    { icon: <GroupIcon color="primary" fontSize="large" />, title: 'Social & Sharing', desc: 'Connect with friends, share routines, and motivate each other.' },
    { icon: <EmojiEventsIcon color="primary" fontSize="large" />, title: 'Routine Management', desc: 'Create, edit, and share structured workout routines.' },
    { icon: <TranslateIcon color="primary" fontSize="large" />, title: 'Multi-language', desc: 'Available in English and Spanish.' },
    { icon: <LockIcon color="primary" fontSize="large" />, title: 'Secure Login', desc: 'Sign in securely with Google.' },
    { icon: <DevicesIcon color="primary" fontSize="large" />, title: 'Responsive Design', desc: 'Works beautifully on desktop and mobile.' }
  ],
  es: [
    { icon: <FitnessCenterIcon color="primary" fontSize="large" />, title: 'Registro de Entrenamientos', desc: 'Registra ejercicios con series, repeticiones y peso.' },
    { icon: <CalendarMonthIcon color="primary" fontSize="large" />, title: 'Vista de Calendario', desc: 'Visualiza tu historial de entrenamientos en un calendario mensual.' },
    { icon: <BarChartIcon color="primary" fontSize="large" />, title: 'Análisis de Entrenamiento IA', desc: 'Obtén análisis inteligentes de tus entrenamientos y progreso.' },
    { icon: <GroupIcon color="primary" fontSize="large" />, title: 'Social y Compartir', desc: 'Conecta con amigos, comparte rutinas y motívense juntos.' },
    { icon: <EmojiEventsIcon color="primary" fontSize="large" />, title: 'Gestión de Rutinas', desc: 'Crea, edita y comparte rutinas de entrenamiento estructuradas.' },
    { icon: <TranslateIcon color="primary" fontSize="large" />, title: 'Multi-idioma', desc: 'Disponible en inglés y español.' },
    { icon: <LockIcon color="primary" fontSize="large" />, title: 'Inicio de Sesión Seguro', desc: 'Inicia sesión de forma segura con Google.' },
    { icon: <DevicesIcon color="primary" fontSize="large" />, title: 'Diseño Responsivo', desc: 'Funciona perfectamente en escritorio y móvil.' }
  ]
};

const marketingText = {
  en: {
    headline: 'Welcome to Lift IQ',
    sub: 'Your all-in-one fitness companion. Track workouts, analyze progress, connect with friends, and achieve your goals with AI-powered insights.',
    cta: 'Sign In / Sign Up with Google',
    signin: 'Sign In / Sign Up',
    signout: 'Sign Out',
    dialogTitle: 'Sign In / Sign Up',
    close: 'Close'
  },
  es: {
    headline: 'Bienvenido a Lift IQ',
    sub: 'Tu compañero fitness todo en uno. Registra entrenamientos, analiza tu progreso, conecta con amigos y alcanza tus metas con inteligencia artificial.',
    cta: 'Iniciar Sesión / Registrarse con Google',
    signin: 'Iniciar Sesión / Registrarse',
    signout: 'Cerrar Sesión',
    dialogTitle: 'Iniciar Sesión / Registrarse',
    close: 'Cerrar'
  }
};

export interface WelcomePageProps {
  isExchangingToken?: boolean;
  scriptError?: string | null;
  isAuthenticated?: boolean;
  onSignOut?: () => void;
}

// Module-level flag to ensure Google is only initialized once
let googleInitialized = false;

export const WelcomePage: React.FC<WelcomePageProps> = ({ isExchangingToken = false, scriptError, isAuthenticated = false, onSignOut }) => {
  const theme = useTheme();
  const [lang, setLang] = useState<'en' | 'es'>(localStorage.getItem('welcome_lang') === 'en' ? 'en' : 'es');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [googleButtonReady, setGoogleButtonReady] = useState(false);
  const googleButtonDialogRef = useRef<HTMLDivElement>(null);
  const { exchangeGoogleToken } = useAuth();

  const handleLangChange = (_: any, newLang: 'en' | 'es') => {
    if (newLang) {
      setLang(newLang);
      localStorage.setItem('welcome_lang', newLang);
    }
  };

  const handleSignInClick = () => setDialogOpen(true);
  const handleDialogClose = () => setDialogOpen(false);
  const handleSignOutClick = () => {
    if (onSignOut) onSignOut();
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;
    setGoogleButtonReady(false); // Reset when dialog opens
    if (
      dialogOpen &&
      window.google &&
      window.google.accounts &&
      window.google.accounts.id &&
      !isAuthenticated
    ) {
      if (!googleInitialized) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: async (response: any) => {
            await exchangeGoogleToken(response.credential);
            window.location.reload();
          },
        });
        googleInitialized = true;
      }
      timeout = setTimeout(() => {
        if (googleButtonDialogRef.current) {
          googleButtonDialogRef.current.innerHTML = '';
          window.google.accounts.id.renderButton(googleButtonDialogRef.current, {
            theme: 'outline',
            size: 'large',
          });
          setGoogleButtonReady(true);
        }
      }, 0);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [dialogOpen, isAuthenticated, exchangeGoogleToken]);

  return (
    <Container maxWidth="md">
      <Paper
        elevation={3}
        sx={{
          mt: 6,
          mb: 4,
          p: { xs: 2, sm: 4 },
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.light, 0.08)} 100%)`,
          borderRadius: 4,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Top bar: Language selector and sign in/up or sign out button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <ToggleButtonGroup
            value={lang}
            exclusive
            onChange={handleLangChange}
            size="small"
            sx={{ background: alpha(theme.palette.background.paper, 0.7), borderRadius: 2 }}
          >
            <ToggleButton value="en">EN</ToggleButton>
            <ToggleButton value="es">ES</ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ minWidth: 180, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            {!isAuthenticated ? (
              <Button variant="contained" color="primary" onClick={handleSignInClick}>
                {marketingText[lang].signin}
              </Button>
            ) : (
              <Button variant="outlined" color="primary" onClick={handleSignOutClick}>
                {marketingText[lang].signout}
              </Button>
            )}
          </Box>
        </Box>
        <Divider sx={{ mb: 3 }} />
        {/* Main headline and subtext */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <FitnessCenterIcon sx={{ fontSize: 60, color: theme.palette.primary.main, mb: 1 }} />
          <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
            {marketingText[lang].headline}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            {marketingText[lang].sub}
          </Typography>
        </Box>
        {/* Features grid */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {featureList[lang].map((feature, idx) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={feature.title}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
                {feature.icon}
                <Typography variant="subtitle1" fontWeight="bold" align="center" sx={{ mt: 1 }}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  {feature.desc}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
        {/* Call to action (Google sign in) - now only in dialog */}
        <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="xs" fullWidth>
          <DialogTitle>{marketingText[lang].dialogTitle}</DialogTitle>
          <DialogContent>
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 80 }}>
              {!googleButtonReady && <CircularProgress size={24} />}
              <Box ref={googleButtonDialogRef} style={{ display: googleButtonReady ? 'block' : 'none' }} />
            </Box>
            {isExchangingToken && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {scriptError && (
              <Typography color="error" align="center" sx={{ mt: 2 }}>
                {scriptError}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>{marketingText[lang].close}</Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
}; 